/**
 * The one place a mark becomes text, and the one place typed text becomes a mark.
 *
 * A mark is exactly ONE thing: a raw score out of the assessment's `max`, or a
 * non-graded status. There is no display mode, because there is nothing left to
 * choose between. A K/T/C/A split changes only how many boxes that raw score is
 * spread across - each section score is still raw, out of its own section max,
 * and still typed as itself.
 *
 * WHAT WAS HERE BEFORE, AND WHY IT WENT. The sheet carried a Raw / % toggle, and
 * `parseMark` accepted five costumes: a plain number, a percent with a `%`
 * suffix, an achievement level (`1`...`4+`), a letter grade (`A-`...`D+`) and
 * `R`. Two problems, the second of them serious:
 *
 *   1. Teachers could not tell which mode they were in. The toggle sat up in a
 *      toolbar and the cells gave no hint, so the same keystroke meant different
 *      things on different days.
 *   2. The parser consulted its LEVEL table BEFORE its numeric branch. In Raw
 *      mode, on an assessment out of 40, typing `3` stored 73% and `4` stored
 *      87%. Every single-digit raw score 1-4 was silently corrupted the moment
 *      it was entered, and the cell then displayed a number nobody had typed.
 *
 * Deleting the level, letter, `R` and `%`-suffix paths kills that whole class of
 * bug outright, rather than reordering the branches and hoping. A typed number
 * now means itself, always.
 *
 * Percent still exists, but only as a DERIVED value: `markPercent()` in
 * `grading.js` computes `score / max * 100` for the weighted average, and the
 * Grade column renders a computed percent and letter. Nothing persists one -
 * `entries.percent` was dropped by the backend migration `drop_entry_percent`.
 */

import { SECTION_SCORE_FIELD, marked, percentToLetter, rolledUp, sectionsOf } from "./grading.js";

const round = (n) => Math.round(n * 10) / 10;

/**
 * A mark as the cell shows it: the NUMERATOR only.
 *
 * The denominator belongs to the column, not to every cell underneath it - a
 * mark cell is 46px wide in the condensed view, and "34/40" does not fit there
 * once, let alone in every row of every column.
 *
 * `section` names which cell is asking: `"K"` / `"T"` / `"C"` / `"A"` for one
 * section of a split, or nothing at all for a single-mark cell or a split's
 * TOTAL. It exists for one rule - a status NAMES ITSELF ONCE. A status belongs
 * to the whole cell and cascades over every section, because a student who was
 * away missed the test, not its Thinking section; printing `Ex` four times
 * across the row would read as four separate decisions taken section by section.
 * So the sections show an en dash and only the total says the word.
 *
 * A split's total is the rolled-up sum, shown even when only some sections are
 * in. It is the one number on the row that can mislead - it sums just what has
 * been marked - and the sheet answers that with a corner dot rather than by
 * blanking the cell, since blanking would hide marks the teacher did enter.
 * `markPercent` independently refuses to average a part-marked assessment, so
 * the misleading number never reaches a grade.
 */
export function formatMark(mark, assessment, section) {
  if (!mark) return null;

  if (section) {
    if (mark.status === "missing" || mark.status === "excused") return "–";
    const score = mark[SECTION_SCORE_FIELD[section]];
    return score == null ? "" : String(round(score));
  }

  if (mark.status === "missing") return "M";
  if (mark.status === "excused") return "Ex";

  if (sectionsOf(assessment).length) {
    return marked(mark, assessment) === 0 ? "" : String(round(rolledUp(mark, assessment)));
  }
  return mark.score == null ? "" : String(round(mark.score));
}

/** A term/running average, in the format that grade's report card accepts. */
export function formatReportGrade(percent, reportFormat) {
  if (percent == null) return null;
  return reportFormat === "letter" ? percentToLetter(percent) : `${Math.round(percent)}%`;
}

/**
 * The `score` column is `Decimal(6,2)`, and the backend 400s anything past it.
 * Catching it here keeps a doomed value off the screen instead of showing it and
 * then failing the write.
 *
 * Deliberately NOT bounded by the assessment's `max`: a score above `max` is a
 * bonus mark, not a typo, and 35 out of 30 must be enterable.
 */
const MAX_SCORE = 9999.99;

/**
 * Parse what the teacher typed into a mark, or into one of two non-marks.
 *
 * Accepts a raw number - decimals included, `34.5` out of 40 is ordinary - or a
 * status shorthand. Nothing else. `assessment` is not consulted: `max` is the
 * column's denominator, not a bound on what may be typed into it. Nor is the
 * split shape, because the number a teacher types is the same number either way
 * - the CALLER knows which cell was typed into and so which field the score
 * lands on (`score`, or one of `scoreK`...`scoreA`).
 *
 * Three return values, and the difference between the last two is load-bearing:
 *   - an object   a mark to store
 *   - `null`      the cell was emptied. That is "never marked", which is the
 *                 absence of a row rather than any of the three statuses.
 *   - `undefined` this is not a mark; ignore the input entirely. Collapsing this
 *                 into `null` would let a typo silently erase a grade.
 */
export function parseMark(raw, assessment) {
  const s = String(raw).trim().toUpperCase();
  if (s === "") return null;

  // Checked before the number so a shorthand is never read as a score. `A`/`ABS`
  // sat here too and went out with the `absent` status itself: it did exactly
  // what `excused` does to the average, and said less - excused also covers an
  // IEP exemption, which absent never did. `A` is now simply not a mark.
  if (s === "M" || s === "MISS") return { status: "missing", score: null };
  if (s === "E" || s === "EX")   return { status: "excused", score: null };

  // `Number("3.")` is 3, which is what lets a decimal be typed one key at a
  // time; the draft in the sheet is what stops that partial value being stored.
  const n = Number(s);
  if (Number.isNaN(n) || n < 0 || n > MAX_SCORE) return undefined;
  return { status: "graded", score: n };
}

/**
 * Is this mark worth flagging to the teacher?
 *
 * Returns a message, or null when the mark is unremarkable.
 *
 * A score ABOVE the assessment's max is deliberately NOT rejected — `34/30` is a
 * bonus mark, and the backend accepts it on purpose. But it is also exactly what
 * a typo looks like, and a teacher scanning a column of numbers has no way to
 * tell 34 is out of 30 when the denominator lives in the header. So it is
 * flagged and saved, rather than blocked.
 *
 * Negative scores never reach here — `parseMark` rejects them outright, because
 * unlike an over-max score there is no reading under which one is meaningful.
 */
export function markWarning(mark, assessment) {
  if (!mark || mark.status !== "graded" || mark.score == null) return null;
  const max = Number(assessment?.max);
  if (!(max > 0) || mark.score <= max) return null;
  return `Mark out of ${max}`;
}
